import { create } from 'zustand';
import { classifySessionFailure, fetchCurrentUser, refreshSession } from '@/lib/auth-refresh';
import { type EffetaApp } from '@/lib/schemas/common.schema';
import { type User } from '@/lib/schemas/user.schema';

// Sesión con Effetá Apps.
//
// El front nunca ve la cookie de sesión (es httpOnly y del host del API):
// al arrancar pide el pase con `POST /auth/refresh`; si responde, pide el
// perfil con `GET /auth/me` y recién ahí muestra la app. El pase vive solo en
// memoria (nada en localStorage) y se renueva un minuto antes de vencer.
// Después de cada renovación se vuelve a pedir `/auth/me`: un cambio de rol
// hecho en Effetá llega con el pase nuevo.

// Tipos públicos del session store ------------------------------------------

// Estado de tours del user. `seen[routeKey] === true` cuando ya vio el
// tour de esa ruta. `neverShow` silencia todos los tours.
export interface TourState {
  seen: Record<string, true>;
  neverShow: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  // Persistido en backend (jsonb en users.tour_state). Source of truth.
  // El front lo mantiene en memoria y hace PATCH atómicos al back para
  // marcar tours como vistos.
  tourState?: TourState;
  // Slug del rol que la persona tiene en Effetá (ej: "full-admin").
  roleSlug?: string | null;
  // `permissions` como `readonly string[]` (no readonly Permission[]): el
  // schema Zod usa un union con `z.string()` fallback para tolerar permisos
  // futuros que el front no conoce todavía. Los gates que usan permissions
  // (filterNavGroups del menú, useCan) reciben string[] y comparan literal.
  permissions?: readonly string[];
  // Áreas asignadas. Combina con `canBypassAreas` en el selector — bypass
  // ignora esta lista y muestra todas.
  areaSlugs?: readonly string[];
  canBypassAreas?: boolean;
}

export type LogoutReason = 'manual' | 'expired' | 'remote';

// Por qué no hay una sesión utilizable cuando no es simplemente "no entró":
// - `no-access`: su rol de Effetá no es un rol del PIM. No se manda a entrar
//   sola: volvería al mismo lugar.
// - `provider-unavailable`: Effetá Apps no contestó al arrancar. La sesión
//   puede seguir abierta; se reintenta solo.
// - `logged-out`: la persona salió (aquí o en otra pestaña). El contrato pide
//   mostrar "Saliste del PIM" y no la entrada: si no, volvería a entrar en el
//   acto. Lo lee el guard, que si no mandaría a /login.
export type SessionIssue = 'no-access' | 'provider-unavailable' | 'logged-out';

export interface SessionState {
  token: string | null;
  user: SessionUser | null;
  // Menú de apps de Effetá de la persona (`apps` del refresh). `null`: nunca
  // llegó en esta sesión. Un refresh sin `apps` conserva la lista anterior:
  // que falte no es "sin apps".
  apps: EffetaApp[] | null;
  isAuthenticated: boolean;
  isBooting: boolean;
  issue: SessionIssue | null;

  // Reemplaza el user completo en memoria.
  setUser: (user: SessionUser) => void;

  // Merge shallow sobre `user.tourState`. Útil para updates optimistas
  // tras PATCH/DELETE al back sin re-fetchear `/auth/me`.
  updateTourState: (patch: Partial<TourState>) => void;

  // Olvida el pase y al user. No llama al back: `POST /auth/logout` lo hace
  // `useLogout` antes de llamar acá.
  logout: (opts?: { reason?: LogoutReason }) => void;

  // Arranque: pase + perfil. Single-flight.
  boot: () => Promise<void>;

  // Renueva el pase (single-flight) y después vuelve a pedir `/auth/me`.
  // Resuelve con el pase nuevo. Si falla, ya dejó la sesión como corresponde
  // (cerrada, "sin acceso" o con un reintento agendado) y rechaza con el
  // error del refresh.
  renew: () => Promise<string>;
}

// Constantes ----------------------------------------------------------------

const BROADCAST_CHANNEL_NAME = 'cef-auth';
// El contrato pide renovar un minuto antes de que venza el pase.
const RENEWAL_MARGIN_SEC = 60;
// Piso del timer en ms — evita re-disparos inmediatos si el back devolviese
// `expires_in` muy chico (ej: testing).
const MIN_RENEWAL_DELAY_MS = 10_000;
// Espera entre reintentos cuando Effetá no contesta (503), hay demasiados
// pedidos (429) o no hay red. El último valor se repite.
export const SESSION_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;

type AuthBroadcastMessage = { type: 'logout'; reason: LogoutReason };

// BroadcastChannel helpers --------------------------------------------------

function createAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (typeof window.BroadcastChannel !== 'function') return null;
  try {
    return new window.BroadcastChannel(BROADCAST_CHANNEL_NAME);
  } catch {
    return null;
  }
}

const authChannel: BroadcastChannel | null = createAuthChannel();

// Timers y single-flight ----------------------------------------------------

// Un único timer activo a la vez: la renovación agendada o el próximo
// reintento. Reagendar pisa el anterior.
let sessionTimer: ReturnType<typeof setTimeout> | null = null;
// Momento en que toca renovar. Lo usa `renewIfDue` cuando la pestaña vuelve
// a estar visible y el timer no corrió (equipo suspendido).
let renewDueAt: number | null = null;
let retryAttempt = 0;
// Cambia cada vez que la sesión se cierra. Un refresh que estaba en vuelo y
// termina después de "Salir" no tiene que revivir la sesión.
let sessionGeneration = 0;

let inFlightBoot: Promise<void> | null = null;
let inFlightRenew: Promise<string> | null = null;

function cancelSessionTimer(): void {
  if (sessionTimer !== null) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
  renewDueAt = null;
}

function setSessionTimer(delayMs: number, run: () => void): void {
  if (sessionTimer !== null) clearTimeout(sessionTimer);
  sessionTimer = setTimeout(() => {
    sessionTimer = null;
    run();
  }, delayMs);
}

function renewInBackground(): void {
  useSessionStore
    .getState()
    .renew()
    .catch(() => {
      /* `renew` ya dejó la sesión como corresponde */
    });
}

function scheduleRenewal(expiresInSec: number): void {
  const delayMs = Math.max((expiresInSec - RENEWAL_MARGIN_SEC) * 1000, MIN_RENEWAL_DELAY_MS);
  setSessionTimer(delayMs, renewInBackground);
  renewDueAt = Date.now() + delayMs;
}

function scheduleRetry(run: () => void): void {
  const index = Math.min(retryAttempt, SESSION_RETRY_DELAYS_MS.length - 1);
  retryAttempt += 1;
  setSessionTimer(SESSION_RETRY_DELAYS_MS[index], run);
}

function toSessionUser(me: User): SessionUser {
  return {
    id: me.id,
    email: me.email,
    firstName: me.firstName,
    lastName: me.lastName,
    roleSlug: me.roleSlug ?? null,
    // El back es source of truth y `permissions: []` es un valor válido,
    // distinto de "no llegó": se copia tal cual.
    permissions: me.permissions,
    areaSlugs: me.areaSlugs,
    canBypassAreas: me.canBypassAreas,
    tourState: me.tourState ?? { seen: {}, neverShow: false },
  };
}

// Perfil después de una renovación. Si falla, la app sigue con el que tenía:
// el próximo ciclo lo vuelve a pedir.
async function reloadUser(token: string, generation: number): Promise<void> {
  try {
    const me = await fetchCurrentUser(token);
    if (generation !== sessionGeneration) return;
    useSessionStore.setState({ user: toSessionUser(me), isAuthenticated: true, issue: null });
  } catch {
    /* ver comentario de arriba */
  }
}

// Store ---------------------------------------------------------------------

// Arranca en `isBooting`: hasta que `/auth/refresh` conteste no se sabe si hay
// sesión, y las route guards muestran el spinner en vez de mandar a entrar.
export const useSessionStore = create<SessionState>()((set, get) => ({
  token: null,
  user: null,
  apps: null,
  isAuthenticated: false,
  isBooting: typeof window !== 'undefined',
  issue: null,

  setUser: (user) => {
    set({ user });
  },

  updateTourState: (patch) => {
    set((state) => {
      if (!state.user) return state;
      const current: TourState = state.user.tourState ?? {
        seen: {},
        neverShow: false,
      };
      const next: TourState = {
        seen: patch.seen ?? current.seen,
        neverShow: patch.neverShow !== undefined ? patch.neverShow : current.neverShow,
      };
      return { user: { ...state.user, tourState: next } };
    });
  },

  logout: (opts) => {
    const reason: LogoutReason = opts?.reason ?? 'manual';

    cancelSessionTimer();
    retryAttempt = 0;
    sessionGeneration += 1;
    set({
      token: null,
      user: null,
      apps: null,
      isAuthenticated: false,
      isBooting: false,
      issue: reason === 'expired' ? null : 'logged-out',
    });

    if (reason !== 'remote' && authChannel) {
      try {
        const message: AuthBroadcastMessage = { type: 'logout', reason };
        authChannel.postMessage(message);
      } catch {
        /* canal cerrado: ignoramos */
      }
    }
  },

  boot: () => {
    // Single-flight: StrictMode dobla el `useEffect` que dispara el boot.
    if (inFlightBoot) return inFlightBoot;
    const generation = sessionGeneration;
    inFlightBoot = (async () => {
      try {
        const res = await refreshSession();
        const me = await fetchCurrentUser(res.access_token);
        if (generation !== sessionGeneration) return;
        retryAttempt = 0;
        scheduleRenewal(res.expires_in);
        set({
          token: res.access_token,
          user: toSessionUser(me),
          apps: res.apps ?? null,
          isAuthenticated: true,
          isBooting: false,
          issue: null,
        });
      } catch (error) {
        if (generation !== sessionGeneration) return;
        const failure = classifySessionFailure(error);
        cancelSessionTimer();
        set({
          token: null,
          user: null,
          apps: null,
          isAuthenticated: false,
          isBooting: false,
          issue:
            failure === 'no-access'
              ? 'no-access'
              : failure === 'transient'
                ? 'provider-unavailable'
                : null,
        });
        if (failure === 'transient') {
          scheduleRetry(() => {
            void get().boot();
          });
        }
      }
    })().finally(() => {
      inFlightBoot = null;
    });
    return inFlightBoot;
  },

  renew: () => {
    // Single-flight: varios pedidos con 401 a la vez comparten un solo
    // refresh (y un solo `/auth/me` después).
    if (inFlightRenew) return inFlightRenew;
    const generation = sessionGeneration;
    inFlightRenew = (async () => {
      try {
        const res = await refreshSession();
        if (generation !== sessionGeneration) return res.access_token;
        retryAttempt = 0;
        scheduleRenewal(res.expires_in);
        set(res.apps ? { token: res.access_token, apps: res.apps } : { token: res.access_token });
        void reloadUser(res.access_token, generation);
        return res.access_token;
      } catch (error) {
        if (generation === sessionGeneration) {
          const failure = classifySessionFailure(error);
          if (failure === 'transient') {
            // La sesión sigue abierta: se sigue con el pase que hay mientras
            // valga y se reintenta.
            scheduleRetry(renewInBackground);
          } else {
            get().logout({ reason: 'expired' });
            if (failure === 'no-access') set({ issue: 'no-access' });
          }
        }
        throw error;
      }
    })().finally(() => {
      inFlightRenew = null;
    });
    return inFlightRenew;
  },
}));

// Renovación al volver a la pestaña: si el timer no corrió (pestaña dormida,
// equipo suspendido) y ya pasó el momento de renovar, renueva ahora en vez de
// esperar al primer 401.
export function renewIfDue(): void {
  if (useSessionStore.getState().token === null) return;
  if (renewDueAt === null || Date.now() < renewDueAt) return;
  renewInBackground();
}

// Suscripción cross-tab al canal de auth ------------------------------------
if (authChannel) {
  authChannel.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as AuthBroadcastMessage | undefined;
    if (!data || data.type !== 'logout') return;
    useSessionStore.getState().logout({ reason: 'remote' });
  });
}

// Dev-only: expone el store en `window.__session` para inspección rápida
// desde la consola del browser. NO se incluye en builds de producción.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __session: typeof useSessionStore }).__session = useSessionStore;
}
